import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Tag, Typography, Spin, Alert, Divider, Button } from 'antd';
import { sheepApi } from '../api';
import { Sheep } from '../types';
import PedigreeTree from '../components/PedigreeTree';
import EventsJournal from '../components/EventsJournal';
import PhotoGallery from '../components/PhotoGallery';

const statusLabels: Record<string, string> = { alive: 'Живой', for_sale: 'В продаже', sold: 'Продан', deceased: 'Погиб' };
const statusColors: Record<string, string> = { alive: 'green', for_sale: 'orange', sold: 'blue', deceased: 'red' };

export default function PublicSheepPage() {
  const { id } = useParams<{ id: string }>();
  const [sheep, setSheep] = useState<Sheep | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    sheepApi.getPublic(Number(id))
      .then((r) => setSheep(r.data))
      .catch(() => setError('Баран не найден'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Spin size="large" />
    </div>
  );

  if (error || !sheep) return (
    <div style={{ maxWidth: 480, margin: '80px auto', padding: 16 }}>
      <Alert type="error" message={error || 'Баран не найден'} />
    </div>
  );

  const ageText = sheep.age ? `${sheep.age.years} лет ${sheep.age.months} мес.` : '—';
  const hasPedigree = !!(sheep.father || sheep.mother ||
    (sheep.childrenAsFather?.length ?? 0) + (sheep.childrenAsMother?.length ?? 0) > 0);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '16px 12px',
    }}>
      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        {/* Основная карточка */}
        <Card
          style={{ borderRadius: 16, marginBottom: 16 }}
          title={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 40 }}>🐑</div>
              <Typography.Title level={3} style={{ margin: 0 }}>
                {sheep.gender === 'male' ? '♂' : '♀'} {sheep.name}
              </Typography.Title>
              <Tag color="purple" style={{ marginTop: 4 }}>{sheep.tagNumber}</Tag>
              <Tag color={statusColors[sheep.status]}>{statusLabels[sheep.status]}</Tag>
            </div>
          }
        >
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Статус">
              <Tag color={statusColors[sheep.status]}>{statusLabels[sheep.status]}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Дата рождения">
              {new Date(sheep.birthDate).toLocaleDateString('ru-RU')}
            </Descriptions.Item>
            <Descriptions.Item label="Возраст">{ageText}</Descriptions.Item>
            <Descriptions.Item label="Пол">
              {sheep.gender === 'male' ? 'Самец (баран)' : 'Самка (овца)'}
            </Descriptions.Item>
            {sheep.breed && <Descriptions.Item label="Порода">{sheep.breed}</Descriptions.Item>}
            {sheep.weight && <Descriptions.Item label="Вес">{sheep.weight} кг</Descriptions.Item>}
            {sheep.bloodline && <Descriptions.Item label="Линия / кровь">{sheep.bloodline}</Descriptions.Item>}
            {(sheep.status === 'for_sale' || sheep.status === 'sold') && sheep.salePrice && (
              <Descriptions.Item label="Цена продажи">
                <b style={{ color: '#389e0d' }}>{Number(sheep.salePrice).toLocaleString('ru-RU')} сом</b>
              </Descriptions.Item>
            )}
            {sheep.earLength && <Descriptions.Item label="Длина ушей">{sheep.earLength} см</Descriptions.Item>}
            {sheep.withersHeight && <Descriptions.Item label="Рост в холке">{sheep.withersHeight} см</Descriptions.Item>}
            {sheep.boneStructure && <Descriptions.Item label="Костяк">{sheep.boneStructure} см</Descriptions.Item>}
            {sheep.chestGirth && <Descriptions.Item label="Обхват груди">{sheep.chestGirth} см</Descriptions.Item>}
            {sheep.fattailGirth && <Descriptions.Item label="Обхват курдюка">{sheep.fattailGirth} см</Descriptions.Item>}
            {sheep.bodyLength && <Descriptions.Item label="Длина">{sheep.bodyLength} см</Descriptions.Item>}
            {sheep.father && (
              <Descriptions.Item label="Отец">
                <a href={`/sheep/${sheep.father.id}`}>
                  {sheep.father.name} ({sheep.father.tagNumber})
                </a>
              </Descriptions.Item>
            )}
            {sheep.mother && (
              <Descriptions.Item label="Мать">
                <a href={`/sheep/${sheep.mother.id}`}>
                  {sheep.mother.name} ({sheep.mother.tagNumber})
                </a>
              </Descriptions.Item>
            )}
            {sheep.gender === 'female' && sheep.lambingNumber && (
              <Descriptions.Item label="Окот по счёту">
                <Tag color="purple">{sheep.lambingNumber}-й окот</Tag>
              </Descriptions.Item>
            )}
            {sheep.gender === 'female' && sheep.conceptionDate && (() => {
              const from = dayjs(sheep.conceptionDate).add(147, 'day').format('DD.MM.YYYY');
              const to   = dayjs(sheep.conceptionDate).add(150, 'day').format('DD.MM.YYYY');
              return (
                <>
                  <Descriptions.Item label="Дата зачатия">
                    {new Date(sheep.conceptionDate).toLocaleDateString('ru-RU')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Примерная дата родов">
                    <Tag color="green">🐣 {from} — {to}</Tag>
                  </Descriptions.Item>
                </>
              );
            })()}
            {sheep.notes && <Descriptions.Item label="Заметки">{sheep.notes}</Descriptions.Item>}
          </Descriptions>
          <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 16 }}>
            Ферма Арашан
          </Typography.Text>
        </Card>

        {/* Галерея */}
        <Card style={{ borderRadius: 16, marginBottom: 16 }} title={
          <span style={{ fontSize: 16, fontWeight: 600 }}>📷 Фотографии</span>
        }>
          <PhotoGallery sheepId={sheep.id} readOnly />
        </Card>

        {/* Родословная */}
        {hasPedigree && (
          <Card
            style={{ borderRadius: 16, marginBottom: 16 }}
            title={<span style={{ fontSize: 16, fontWeight: 600 }}>🌳 Родословная</span>}
            extra={
              <Button
                size="small"
                onClick={() => window.location.href = `/sheep/${sheep.id}/pedigree?public=1`}
              >
                Полная родословная
              </Button>
            }
          >
            <div style={{ overflowX: 'auto' }}>
              <PedigreeTree sheep={sheep} readOnly />
            </div>
          </Card>
        )}

        {/* Журнал событий */}
        <Card style={{ borderRadius: 16 }} title={
          <span style={{ fontSize: 16, fontWeight: 600 }}>📋 Журнал событий</span>
        }>
          <EventsJournal sheepId={sheep.id} readOnly />
        </Card>
      </div>
    </div>
  );
}
